<template>
    <div class="ftu-overlay">
        <div class="ftu-modal" role="dialog" aria-modal="true" aria-labelledby="ftu-title">
            <div class="ftu-icon">
                <div class="ftu-icon-circle">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path d="M13 2L4.5 13.5H11l-1 8.5 8.5-11.5H12l1-8.5z" fill="#fff" stroke="#fff"
                            stroke-width="1.5" stroke-linejoin="round" />
                    </svg>
                </div>
            </div>

            <h2 id="ftu-title" class="ftu-title">Want to skip the line?</h2>
            <p class="ftu-sub">Your order is confirmed. One optional upgrade before you go.</p>

            <div class="ftu-card">
                <div class="ftu-card-head">
                    <span class="ftu-pill">Priority</span>
                    <span class="ftu-price">+{{ formattedFee }}</span>
                </div>
                <div class="ftu-card-title">{{ addon.product.name }}</div>
                <ul class="ftu-list">
                    <li>Your intake moves to the front of the review queue</li>
                    <li>Reviewed by a licensed physician on a priority basis</li>
                    <li>Your medication ships sooner</li>
                </ul>
                <p class="ftu-note">{{ noteText }}</p>
            </div>

            <!-- Card entry. Only the Klarna path opens it up front; a wallet order
                 opens it after its own card is refused, and a card order never does. -->
            <div v-if="showCard" class="ftu-cardform">
                <div>
                    <div class="ftu-cardform-label">Card details</div>
                    <stripe-payment-element ref="cardEntry" element-id="ftu-payment-element"
                        :base-url="baseUrl" :user-id="context.userId" :user-name="cardholderName"
                        :user-email="context.email || ''" hide-address-fields :billing-address="billing"
                        @ready="hasCardInput = $event" @token="onCardToken" @error="onCardError" />
                </div>

                <div class="ftu-cardform-section">
                    <div class="ftu-cardform-label">Billing address</div>
                    <div v-if="!editingAddress" class="ftu-addr">
                        <span class="ftu-addr-text">{{ formattedAddress }}</span>
                        <button type="button" class="ftu-addr-change" @click="editingAddress = true">Change</button>
                    </div>
                    <input v-else type="text" class="ftu-input" placeholder="Address line 1"
                        autocomplete="billing address-line1" v-model="billing.addressLine1">
                </div>
                <template v-if="editingAddress">
                    <input type="text" class="ftu-input" placeholder="Apt, suite (optional)"
                        autocomplete="billing address-line2" v-model="billing.addressLine2">
                    <div class="ftu-cardform-row">
                        <input type="text" class="ftu-input" placeholder="City" autocomplete="billing address-level2"
                            v-model="billing.city">
                        <input type="text" class="ftu-input" placeholder="State"
                            autocomplete="billing address-level1" v-model="billing.state">
                        <input type="text" class="ftu-input" placeholder="ZIP" inputmode="numeric"
                            autocomplete="billing postal-code" v-model="billing.zip">
                    </div>
                </template>
            </div>

            <div v-if="errorMessage" class="ftu-error">{{ errorMessage }}</div>

            <button type="button" class="ftu-btn ftu-btn-yes" :disabled="charging" @click="accept">
                <span class="ftu-btn-stack">
                    <span>{{ showCard ? 'Add priority review' : 'Yes, jump me to the front of the line' }}</span>
                    <small>{{ charging ? 'Processing…' : 'Add for ' + formattedFee }}</small>
                </span>
            </button>

            <button type="button" class="ftu-btn ftu-btn-no" :disabled="charging" @click="$emit('done')">
                No, I'll take standard review
            </button>
        </div>
    </div>
</template>

<script>
/* ------------------------------------------------------------------ */
/*  Priority Physician Review — the post-purchase offer                 */
/*                                                                      */
/*  Shown once, on top of an order the server has already confirmed,    */
/*  before the checkout leaves for its thank-you route. Two buttons:    */
/*  yes charges the card already on file, no continues untouched. It    */
/*  replaced the pre-ticked checkbox inside checkout, which cost        */
/*  conversion on the order itself.                                     */
/*                                                                      */
/*  How the patient pays for the add-on follows how they paid for the   */
/*  order, and there are three cases:                                   */
/*                                                                      */
/*    card              — a card is on file. One tap, billed with       */
/*                        `useExistingCard`. No fields, ever.           */
/*    apple_pay/        — the wallet sent a real Stripe payment method  */
/*    google_pay          to /api/checkout-stripe, exactly as a typed   */
/*                        card does, so the add-on TRIES that card      */
/*                        first. Only if that charge comes back failed  */
/*                        does it reveal fields — whether the backend   */
/*                        keeps a wallet card billable is its call, not */
/*                        something to assume in either direction.      */
/*    klarna            — authorized one fixed amount up front and left */
/*                        no card at all, so the first yes opens the    */
/*                        fields and the second charges them.           */
/*                                                                      */
/*  Nothing in here can touch the order it rides on. A decline, a bad   */
/*  card, a timeout or a dead endpoint leaves a message in the modal    */
/*  and the patient free to say no — the worst case is an add-on they   */
/*  didn't get, never a prescription they didn't get.                   */
/*                                                                      */
/*  Deliberately not dismissable by overlay click or Escape: the two    */
/*  buttons are the only way out, so "no" is always an explicit answer  */
/*  and a stray tap can never leave a paid patient staring at a modal.  */
/*                                                                      */
/*  Tracking is NOT fired here: every checkout numbers its own funnel   */
/*  steps, so the parent fires the add-on's `purchase` from @purchased. */
/* ------------------------------------------------------------------ */

import StripePaymentElement from '../common/StripePaymentElement.vue'
import { chargeFastTrackUpsell } from '@/utils/fastTrackUpsell.js'

const EMPTY_ADDRESS = { addressLine1: '', addressLine2: '', city: '', state: '', zip: '' }

export default {
    name: 'FastTrackUpsellModal',
    components: {
        StripePaymentElement
    },
    props: {
        // { product, plan } from resolveFastTrackAddon. The parent only mounts
        // this component once that is non-null, so the offer is real.
        addon: {
            type: Object,
            required: true
        },
        // How the order this rides on was paid: 'card', 'apple_pay',
        // 'google_pay' or 'klarna'. See the three cases above.
        paymentMethod: {
            type: String,
            default: 'card'
        },
        // Who is charged and where:
        // { userId, paymentTerms, firstName, lastName, email,
        //   shippingAddress, billingAddress, trackingSessionId, ipAddress }
        context: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            baseUrl: process.env.VUE_APP_API_ENDPOINT,
            showCard: false,
            charging: false,
            errorMessage: '',
            hasCardInput: false,
            editingAddress: false,
            // Filled by the Payment Element's own token event — display only.
            cardBrand: '',
            cardLast4: '',
            // Prefilled from the order's own billing address the first time the
            // fields open. Never invented — a patient with nothing on file types
            // it themselves.
            billing: { ...EMPTY_ADDRESS }
        }
    },
    computed: {
        fee() {
            return Number(Number(this.addon.plan.price).toFixed(2))
        },
        formattedFee() {
            return `$${this.fee.toFixed(2)}`
        },
        /** Klarna alone leaves nothing billable behind, so it alone asks up front. */
        cardUpFront() {
            return this.paymentMethod === 'klarna'
        },
        /** The wallets charge the order's own card first and only ask if that fails. */
        walletFallback() {
            return this.paymentMethod === 'apple_pay' || this.paymentMethod === 'google_pay'
        },
        paymentMethodLabel() {
            if (this.paymentMethod === 'apple_pay') return 'Apple Pay'
            if (this.paymentMethod === 'google_pay') return 'Google Pay'
            return 'Klarna'
        },
        cardholderName() {
            return `${this.context.firstName || ''} ${this.context.lastName || ''}`.trim()
        },
        noteText() {
            if (this.showCard) {
                return `One-time ${this.formattedFee} charge to the card below. Your ${this.paymentMethodLabel} order is not changed. Standard review is always included free.`
            }
            if (this.cardUpFront) {
                return `One-time ${this.formattedFee} charge. Klarna cannot be used for add-ons, so this is billed to a card you enter next. Standard review is always included free.`
            }
            return `One-time ${this.formattedFee} charge to the card you just used. Standard review is always included free.`
        },
        /** "123 Main St, Apt 4B, Los Angeles, CA 90001" */
        formattedAddress() {
            const street = [this.billing.addressLine1, this.billing.addressLine2].filter(Boolean).join(', ')
            return [street, this.billing.city, `${this.billing.state} ${this.billing.zip}`.trim()]
                .filter(Boolean)
                .join(', ')
        }
    },
    mounted() {
        // Tells the parent the offer is really on screen. The parent is holding a
        // paid customer's redirect until it hears this, and gives up if it doesn't.
        this.$emit('shown')
    },
    methods: {
        isCompleteAddress(address) {
            return Boolean(address && address.addressLine1 && address.city && address.state && address.zip)
        },
        // The order's own billing address is already on file and already valid, so
        // it is shown as one line rather than five fields to retype. It is still
        // sent for AVS. Only an incomplete address opens the fields on its own —
        // and "Change" is there for a card billed somewhere else, since an address
        // the card doesn't match is a decline.
        openCardFields() {
            this.billing = { ...EMPTY_ADDRESS, ...(this.context.billingAddress || {}) }
            this.editingAddress = !this.isCompleteAddress(this.billing)
            this.showCard = true
        },
        onCardError(message) {
            this.errorMessage = message || 'Please check your card details.'
        },
        // Fires synchronously from inside confirmPayment(), so brand and last4 are
        // already set by the time the awaited call returns.
        onCardToken(paymentMethodId, cardDetails) {
            this.cardBrand = (cardDetails && cardDetails.cardBrand) || ''
            this.cardLast4 = (cardDetails && cardDetails.cardLast4) || ''
        },
        async accept() {
            if (this.charging) return

            // On the Klarna path the first yes only opens the fields: there is no
            // card on file, so no version of this charge can skip collecting one.
            if (this.cardUpFront && !this.showCard) {
                this.errorMessage = ''
                this.openCardFields()
                return
            }

            let cardToken = ''
            let cardBrand = ''
            let cardLast4 = ''

            if (this.showCard) {
                if (!this.hasCardInput) {
                    this.errorMessage = 'Please enter your card details.'
                    return
                }
                if (!this.isCompleteAddress(this.billing)) {
                    this.errorMessage = 'Please enter a complete billing address.'
                    this.editingAddress = true
                    return
                }

                this.charging = true
                this.errorMessage = ''

                // Returns the pm_ id, or null after emitting @error into errorMessage.
                const paymentMethodId = await this.$refs.cardEntry.confirmPayment()
                if (!paymentMethodId) {
                    this.charging = false
                    if (!this.errorMessage) this.errorMessage = 'That card could not be verified.'
                    return
                }
                cardToken = paymentMethodId
                cardBrand = this.cardBrand
                cardLast4 = this.cardLast4
            } else {
                this.charging = true
                this.errorMessage = ''
            }

            const result = await chargeFastTrackUpsell({
                addon: this.addon,
                userId: this.context.userId,
                paymentTerms: this.context.paymentTerms,
                firstName: this.context.firstName,
                lastName: this.context.lastName,
                email: this.context.email,
                // The card path sends no token at all, so the backend bills the one
                // the order was just placed with. Klarna sends the token collected
                // above, with the address the patient confirmed for that card.
                cardToken,
                cardBrand,
                cardLast4,
                billingAddress: this.showCard ? this.billing : this.context.billingAddress,
                shippingAddress: this.context.shippingAddress,
                trackingSessionId: this.context.trackingSessionId,
                ipAddress: this.context.ipAddress
            })

            this.charging = false

            if (result.ok) {
                this.$emit('purchased', this.addon, result)
                this.$emit('done')
                return
            }

            if (this.walletFallback && !this.showCard) {
                // The wallet's own card was refused for the add-on. Rather than end
                // the offer there, open the fields with the order's billing address
                // prefilled — the next yes charges what the patient types.
                this.openCardFields()
                this.errorMessage = `We couldn't charge the card from your ${this.paymentMethodLabel} order for this add-on. Enter a card below to add it.`
                return
            }

            this.errorMessage = result.message
        }
    }
}
</script>

<style scoped>
/* Values are the checkout pages' own --ns-* palette (Checkout-glp1-v5.vue) and
   their green CTA gradient, redeclared here because this modal renders as its
   own component and never inherits .checkout-page's variables. */
.ftu-overlay {
    --ftu-teal: #08466f;
    --ftu-soft-blue: #c9e8ff;
    --ftu-green: #10b981;
    --ftu-green-dark: #059669;
    --ftu-gray-50: #f9fafb;
    --ftu-gray-200: #e5e7eb;
    --ftu-gray-300: #d1d5db;
    --ftu-gray-400: #9ca3af;
    --ftu-gray-500: #6b7280;
    --ftu-gray-600: #4b5563;
    --ftu-gray-700: #374151;
    --ftu-gray-900: #111827;

    position: fixed;
    inset: 0;
    z-index: 99999999;
    background: rgba(0, 0, 0, 0.6);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    animation: ftu-fade-in 0.25s ease;
}

@keyframes ftu-fade-in {
    from { opacity: 0; }
    to { opacity: 1; }
}

.ftu-modal {
    background: #fff;
    border-radius: 16px;
    padding: 28px 24px 24px;
    max-width: 420px;
    width: 100%;
    max-height: 92vh;
    overflow-y: auto;
    text-align: center;
    animation: ftu-pop-in 0.4s cubic-bezier(0.16, 1, 0.3, 1);
    font-family: Satoshi, Arial, sans-serif;
    box-sizing: border-box;
}

.ftu-modal *,
.ftu-modal *::before,
.ftu-modal *::after {
    box-sizing: border-box;
}

@keyframes ftu-pop-in {
    from { opacity: 0; transform: scale(0.9) translateY(16px); }
    to { opacity: 1; transform: scale(1) translateY(0); }
}

.ftu-icon {
    display: flex;
    justify-content: center;
    margin-bottom: 16px;
}

.ftu-icon-circle {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: var(--ftu-teal);
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 14px rgba(8, 70, 111, 0.35);
    animation: ftu-circle 0.45s cubic-bezier(0.16, 1, 0.3, 1) 0.1s both;
}

@keyframes ftu-circle {
    from { transform: scale(0); opacity: 0; }
    to { transform: scale(1); opacity: 1; }
}

.ftu-title {
    font-size: 23px;
    font-weight: 800;
    letter-spacing: -0.02em;
    color: var(--ftu-gray-900);
    margin: 0 0 6px;
}

.ftu-sub {
    font-size: 14px;
    line-height: 1.5;
    color: var(--ftu-gray-500);
    margin: 0 0 18px;
}

.ftu-card {
    text-align: left;
    background: var(--ftu-gray-50);
    border: 1px solid var(--ftu-gray-200);
    border-radius: 12px;
    padding: 14px 16px 16px;
    margin-bottom: 18px;
}

.ftu-card-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
}

.ftu-pill {
    display: inline-flex;
    align-items: center;
    padding: 4px 9px;
    border-radius: 999px;
    background: var(--ftu-soft-blue);
    color: var(--ftu-teal);
    font-size: 10px;
    font-weight: 900;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    line-height: 1.2;
}

.ftu-price {
    font-size: 20px;
    font-weight: 900;
    letter-spacing: -0.04em;
    color: var(--ftu-gray-900);
    white-space: nowrap;
}

.ftu-card-title {
    margin: 10px 0 0;
    font-size: 16px;
    font-weight: 800;
    letter-spacing: -0.02em;
    color: var(--ftu-gray-900);
}

.ftu-list {
    margin: 10px 0 0;
    padding: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 7px;
}

.ftu-list li {
    position: relative;
    padding-left: 22px;
    font-size: 13.5px;
    line-height: 1.45;
    color: var(--ftu-gray-600);
}

.ftu-list li::before {
    content: "";
    position: absolute;
    left: 4px;
    top: 6px;
    width: 6px;
    height: 10px;
    border: solid var(--ftu-teal);
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
}

.ftu-note {
    margin: 12px 0 0;
    font-size: 12px;
    line-height: 1.5;
    color: var(--ftu-gray-500);
}

.ftu-error {
    margin: 0 0 14px;
    padding: 10px 12px;
    border-radius: 10px;
    background: #fef2f2;
    border: 1px solid #fecaca;
    color: #b91c1c;
    font-size: 13px;
    line-height: 1.45;
    text-align: left;
}

.ftu-cardform {
    text-align: left;
    margin: 0 0 16px;
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.ftu-cardform-label {
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--ftu-gray-500);
    margin: 0 0 6px;
}

.ftu-cardform-section {
    margin-top: 4px;
}

.ftu-cardform-row {
    display: grid;
    grid-template-columns: minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1fr);
    gap: 10px;
}

.ftu-input {
    width: 100%;
    background: #fff;
    border: 1px solid var(--ftu-gray-300);
    border-radius: 10px;
    padding: 12px;
    font-size: 15px;
    color: var(--ftu-gray-900);
    min-height: 44px;
    font-family: inherit;
    outline: none;
    -webkit-appearance: none;
    appearance: none;
}

.ftu-input:focus {
    border-color: var(--ftu-teal);
}

.ftu-input::placeholder {
    color: var(--ftu-gray-400);
}

/* The address the order was placed with, shown as one line instead of five
   fields. It still goes to the gateway for AVS — it just isn't retyped. */
.ftu-addr {
    display: flex;
    align-items: baseline;
    gap: 8px;
    justify-content: space-between;
}

.ftu-addr-text {
    font-size: 13px;
    color: var(--ftu-gray-600);
    line-height: 1.45;
}

.ftu-addr-change {
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    flex-shrink: 0;
    font-size: 12px;
    font-weight: 700;
    color: var(--ftu-teal);
    text-decoration: underline;
    font-family: inherit;
}

.ftu-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    width: 100%;
    padding: 16px;
    border: none;
    border-radius: 50px;
    font-size: 15px;
    font-weight: 800;
    font-family: inherit;
    cursor: pointer;
    transition: all 0.2s;
}

.ftu-btn:disabled {
    cursor: not-allowed;
    opacity: 0.6;
}

.ftu-btn-yes {
    background: linear-gradient(135deg, var(--ftu-green) 0%, var(--ftu-green-dark) 100%);
    color: #fff;
}

.ftu-btn-yes:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
}

.ftu-btn-stack {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    line-height: 1.2;
}

.ftu-btn-stack small {
    font-size: 11.5px;
    font-weight: 600;
    opacity: 0.9;
}

.ftu-btn-no {
    margin-top: 10px;
    background: transparent;
    color: var(--ftu-gray-500);
    font-size: 14px;
    font-weight: 700;
    text-decoration: underline;
    padding: 12px 16px;
}

.ftu-btn-no:hover:not(:disabled) {
    color: var(--ftu-gray-700);
}

@media screen and (max-width: 640px) {
    .ftu-overlay {
        align-items: flex-end;
        padding: 0;
    }

    .ftu-modal {
        max-width: 100%;
        max-height: 94vh;
        border-radius: 20px 20px 0 0;
        padding: 24px 20px 30px;
        animation: ftu-sheet-up 0.4s cubic-bezier(0.16, 1, 0.3, 1);
    }

    @keyframes ftu-sheet-up {
        from { transform: translateY(100%); }
        to { transform: translateY(0); }
    }
}
</style>
